
import { BarChart3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DatePicker } from "@/components/ui/date-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { format } from "date-fns";

interface AnalyticsHeaderProps {
  dateRange: {
    from: Date | undefined;
    to: Date | undefined;
  };
  setDateRange: (range: { from: Date | undefined; to: Date | undefined }) => void;
  downloadReport: () => void;
  selectedDataType: string;
  setSelectedDataType: (type: string) => void;
  selectedFormat: string;
  setSelectedFormat: (format: string) => void;
}

export function AnalyticsHeader({ 
  dateRange, 
  setDateRange, 
  downloadReport, 
  selectedDataType, 
  setSelectedDataType, 
  selectedFormat, 
  setSelectedFormat 
}: AnalyticsHeaderProps) {
  const dataTypeOptions = [
    { value: 'revenue-summary', label: 'Revenue Summary', description: 'Doctor revenue and patient counts' },
    { value: 'patient-details', label: 'Patient Details', description: 'Complete patient information' },
    { value: 'doctor-performance', label: 'Doctor Performance', description: 'Doctor KPIs and metrics' },
    { value: 'complete-report', label: 'Complete Report', description: 'All analytics data combined' }
  ];

  const formatOptions = [
    { value: 'csv', label: 'CSV', description: 'Comma-separated values' },
    { value: 'pdf', label: 'PDF', description: 'Portable document format' }
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h1 className="text-2xl font-bold">Financial Analytics</h1>
        <p className="text-gray-500">Track revenue and patient statistics</p>
      </div>
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="flex items-center space-x-2">
          <DatePicker 
            selected={dateRange.from}
            onSelect={(date) => setDateRange({ from: date, to: dateRange.to })}
            disabled={(date) => date > new Date()}
          />
          <span className="text-sm text-muted-foreground">to</span>
          <DatePicker 
            selected={dateRange.to}
            onSelect={(date) => setDateRange({ from: dateRange.from, to: date })}
            disabled={(date) => date > new Date() || (dateRange.from && date < dateRange.from)}
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Select value={selectedDataType} onValueChange={setSelectedDataType}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select data type" />
            </SelectTrigger>
            <SelectContent>
              {dataTypeOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  <div>
                    <div className="font-medium">{option.label}</div>
                    <div className="text-xs text-muted-foreground">{option.description}</div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={selectedFormat} onValueChange={setSelectedFormat}>
            <SelectTrigger className="w-[100px]">
              <SelectValue placeholder="Format" />
            </SelectTrigger>
            <SelectContent>
              {formatOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  <div>
                    <div className="font-medium">{option.label}</div>
                    <div className="text-xs text-muted-foreground">{option.description}</div>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button 
            onClick={downloadReport} 
            disabled={!selectedDataType || !selectedFormat}
            className="bg-hospital-primary hover:bg-hospital-primary/90"
          >
            <BarChart3 className="mr-2 h-4 w-4" />
            Export Report
          </Button>
        </div>
      </div>
    </div>
  );
}
